import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View, Keyboard } from 'react-native';
import emoji from 'node-emoji';
import { CommonActions } from '@react-navigation/native';
import { noop } from 'lodash';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import ShakeContainer from 'design-system/ShakeContainer';
import Text, { styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { saveNote } from 'actions/transactions';
import TextInput from 'utils/packages/TextInput';
import createStyleSheets from 'utils/createStyleSheets';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { isValidUserInputMultilineString } from 'utils/validation';
import { triggerInputErrorHapticFeedback } from 'utils/hapticFeedback';
import { muteAll, useFetchOld } from 'hooks/useFetch';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { isWeb } from '../../../constants';
import { useTransaction } from '../hooks/useTransaction';
const placeholders = [
    emoji.emojify('Spending all my :moneybag:!'),
    emoji.emojify(':pizza: with friends.'),
    emoji.emojify(':hamburger: :fries: :icecream:'),
    emoji.emojify('My terrific weekend! :tada: :confetti_ball:'),
    emoji.emojify('I :heart: :coffee:!'),
    emoji.emojify(':cloud: :ok_woman: :cloud:'),
    emoji.emojify(':money_with_wings: :money_with_wings: :money_with_wings:'),
    emoji.emojify(':see_no_evil: :hear_no_evil: :speak_no_evil:'),
    emoji.emojify(':sushi: :ramen: :rice_ball:'),
];
const AddNoteScreen = ({ navigation, route, transaction }) => {
    const { id, ids, onSaveCallback, keyBack } = route.params;
    const [note, setNoteState] = useState(transaction?.notes || '');
    const error = useMemo(() => !isValidUserInputMultilineString(note), [note]);
    const shakeCallbackRef = useRef(undefined);
    const styles = useStyles(styleSet);
    const [isSaving, setIsSaving] = useState(false);
    const [, , , fetch] = useFetchOld(muteAll);
    const saveDetails = useCallback(async () => {
        if (error) {
            triggerInputErrorHapticFeedback();
            shakeCallbackRef.current?.();
            return;
        }
        Keyboard.dismiss();
        setIsSaving(true);
        if (id) {
            await fetch(saveNote(id, note.trim()));
        }
        else if (ids) {
            await Promise.all(ids.map((id) => fetch(saveNote(id, note.trim())))).catch(noop);
        }
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.dispatch(CommonActions.reset({
                index: 0,
                routes: [{ name: 'Feed' }],
            }));
        }
        if (ids && ids.length > 1 && keyBack) {
            navigation.navigateDeprecated('LearningScreen', {
                keyBack,
                editData: { editType: 'note' },
                transactionsCount: ids.length,
            });
        }
        onSaveCallback?.();
        setIsSaving(false);
    }, [error, id, ids, onSaveCallback, note, keyBack]);
    const colors = useColors();
    const registerShakeCallback = useCallback((shakeCallback) => {
        shakeCallbackRef.current = shakeCallback;
    }, []);
    const placeholder = useMemo(() => placeholders[Math.floor(Math.random() * placeholders.length)], []);
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    return (<AppView avoidHeader isInTab={isWeb}>
      <KeyboardAvoidingContainerNew flexContainer>
        <StatusBar barStyle="dark-content"/>
        {isWeb ? <Spacer h={72}/> : null}
        <View style={styles.noteContainer}>
          <TextInput multiline value={note} maxLength={1000} ref={textInputRef} testID="textInput" allowFontScaling={false} placeholder={placeholder} onChangeText={setNoteState} underlineColorAndroid="transparent" focusStyle={styles.textInputFocus} selectionColor={colors.text.selection} style={[styles.input, textStyles.Text_16]} placeholderTextColor={colors.text.placeholder}/>
          {error ? (<ShakeContainer shakeOnMount registerCallback={registerShakeCallback}>
              <Spacer.H16 />
              <Text TextThin-14 Red centered>
                {emoji.emojify("Note contains invalid characters '<' and '>' :warning:")}
              </Text>
            </ShakeContainer>) : null}
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title="Save" onPress={saveDetails} isFetching={isSaving} withFrame={false}/>
    </AppView>);
};
const AddNoteScreenWrapper = (props) => {
    const { navigation } = props;
    const { id, ids } = props.route.params;
    const { transaction, LoadingOr404 } = useTransaction(id);
    const colors = useColors();
    const showLoadingOr404 = !transaction && !ids;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Add a note',
            headerTintColor: colors.text.primary,
            headerTitle: showLoadingOr404 ? '' : 'Add a note',
        });
    }, [colors.text.primary, showLoadingOr404, transaction]);
    if (showLoadingOr404) {
        return LoadingOr404;
    }
    return <AddNoteScreen {...props} transaction={transaction}/>;
};
const styleSet = createStyleSheets((colors) => ({
    noteContainer: {
        flex: 1,
        marginVertical: rem(16),
    },
    input: {
        textAlignVertical: 'top',
        ...(isWeb
            ? {
                paddingVertical: rem(16),
                height: rem(160),
                color: colors.text.secondary,
            }
            : {
                flex: 1,
                paddingVertical: 0,
                color: colors.text.primary,
            }),
    },
    textInputFocus: {
        color: colors.text.primary,
    },
}));
export default AddNoteScreenWrapper;
